/**
 * <express-60-toggle>
 * Paid 60-minute Dubai express delivery — a PER-ORDER fee.
 *
 * Flow (no lock, no orphan, no product duplication):
 *  - Turn the toggle ON while the product IS in the cart  → add the AED 50 line live.
 *  - Turn the toggle ON while the product is NOT in cart   → "arm" it (nothing added
 *    yet, so no orphan). As soon as the product is added to the cart, the express
 *    line is added automatically (detected via the cart MutationObserver).
 *  - Turn the toggle OFF → remove the express line (and disarm).
 *  - The toggle never touches product quantities, so it can't duplicate the product.
 *  - The line carries _express_parent_variant_id so the cart shows it as a
 *    warranty-style badge and cart-items.js drops it when the product is removed.
 *  - It mirrors the cart on every re-render (MutationObserver) because removing a
 *    skip-rendered add-on line does NOT fire on:line-item:change.
 *
 * Visibility gates: region (Dubai by default) + availability windows (HH:MM, GST).
 */

function e60ParseHM(value) {
  if (!value) return null;
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

function e60WithinWindow(nowMin, from, to) {
  if (from == null || to == null || from === to) return false;
  if (from < to) return nowMin >= from && nowMin < to;
  return nowMin >= from || nowMin < to;
}

/* "0,6" → [0, 6]; office-closed weekdays (0 = Sunday). */
function e60ParseDayList(value) {
  return String(value || '')
    .split(',')
    .map((d) => parseInt(d.trim(), 10))
    .filter((d) => !isNaN(d) && d >= 0 && d <= 6);
}

if (!customElements.get('express-60-toggle')) {
  class Express60Toggle extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.geoMode = (this.dataset.geoMode || 'off').toLowerCase();
      // Availability windows — show the toggle only inside one of these (GST).
      this.openWindows = [
        { from: e60ParseHM(this.dataset.open1From), to: e60ParseHM(this.dataset.open1To) },
        { from: e60ParseHM(this.dataset.open2From), to: e60ParseHM(this.dataset.open2To) }
      ].filter((w) => w.from != null && w.to != null && w.from !== w.to);
      // Friday override — when any Friday window is configured, Fridays use ONLY these.
      this.fridayWindows = [
        { from: e60ParseHM(this.dataset.friOpen1From), to: e60ParseHM(this.dataset.friOpen1To) },
        { from: e60ParseHM(this.dataset.friOpen2From), to: e60ParseHM(this.dataset.friOpen2To) }
      ].filter((w) => w.from != null && w.to != null && w.from !== w.to);
      // Office-closed weekdays — no 60-minute runs at all on these days.
      this.closedDays = e60ParseDayList(this.dataset.closedDays);
      this.checkbox = this.querySelector('[data-express60-checkbox]');
      this.hideInterval = null;
      this.busy = false;
      this.armed = false; // toggled on before the product was in the cart
      this.geoAllowed = this.geoMode === 'off';
      this.revealed = false;

      this._onChange = this.handleChange.bind(this);
      this._onCartChange = () => this.syncFromCart();
      this._onVariantChange = () => this.syncFromCart();
      // The visitor can change their emirate on this page (delivery-eta picker) or
      // on another page and come back through BFCache — re-evaluate instead of the
      // old irreversible this.remove(), so the toggle can appear/disappear live.
      this._onEmirateChange = () => this.resolveVisibility();
      this._onPageShow = (event) => {
        if (event.persisted) this.resolveVisibility();
      };
      document.addEventListener(this.emirateChangeEvent(), this._onEmirateChange);
      window.addEventListener('pageshow', this._onPageShow);

      // express-delivery.js (window.tbGeo — the shared emirate resolver) is a deferred
      // script too, so if it sits later in the document than this one it has NOT run yet
      // when this element upgrades. Resolving immediately would silently fall through to
      // the raw IP lookup below: the visitor's manual emirate pick is ignored, a Dubai
      // shopper on a Sharjah/Abu Dhabi carrier IP never sees the toggle, and nothing
      // re-evaluates until an emirate change. All deferred scripts have executed by
      // DOMContentLoaded, so wait for it when tbGeo is not there yet.
      if (!window.tbGeo && document.readyState === 'loading') {
        this._onDomReady = () => {
          this._onDomReady = null;
          if (this.isConnected) this.resolveVisibility();
        };
        document.addEventListener('DOMContentLoaded', this._onDomReady, { once: true });
      } else {
        this.resolveVisibility();
      }
    }

    disconnectedCallback() {
      if (this.checkbox) this.checkbox.removeEventListener('change', this._onChange);
      document.removeEventListener('on:cart:add', this._onCartChange);
      document.removeEventListener('on:line-item:change', this._onCartChange);
      document.removeEventListener('on:variant:change', this._onVariantChange);
      document.removeEventListener(this.emirateChangeEvent(), this._onEmirateChange);
      window.removeEventListener('pageshow', this._onPageShow);
      if (this._onDomReady) document.removeEventListener('DOMContentLoaded', this._onDomReady);
      if (this.cartObserver) this.cartObserver.disconnect();
      if (this.hideInterval) clearInterval(this.hideInterval);
      if (this.syncTimer) clearTimeout(this.syncTimer);
    }

    emirateChangeEvent() {
      return (window.tbGeo && window.tbGeo.EMIRATE_CHANGE_EVENT) || 'on:emirate:change';
    }

    async resolveVisibility() {
      let allowed = true;
      if (this.geoMode !== 'off') {
        try {
          allowed = await this.isVisitorAllowed();
        } catch (e) {
          allowed = false;
        }
      }
      this.geoAllowed = allowed;
      if (allowed && !this.revealed) {
        this.revealed = true;
        this.reveal();
      } else {
        this.applyAvailabilityWindow();
      }
    }

    reveal() {
      this.classList.remove('express-60--pending');
      this.checkbox.addEventListener('change', this._onChange);

      document.addEventListener('on:cart:add', this._onCartChange);
      document.addEventListener('on:line-item:change', this._onCartChange);
      document.addEventListener('on:variant:change', this._onVariantChange);

      // Removing a skip-rendered add-on line doesn't fire on:line-item:change,
      // so also watch the cart DOM for any re-render and re-sync (debounced).
      this.observeCart();

      this.applyAvailabilityWindow();
      this.hideInterval = setInterval(() => this.applyAvailabilityWindow(), 30000);

      this.syncFromCart();
    }

    async handleChange() {
      if (this.busy) return;
      this.busy = true;
      const wantOn = this.checkbox.checked;

      try {
        const items = await this.cartItems();

        if (wantOn) {
          if (this.hasProduct(items)) {
            if (!this.hasExpress(items)) await this.addToCart();
            this.armed = false;
          } else {
            // No product yet — arm. Express is added once the product is in cart.
            this.armed = true;
          }
          document.documentElement.classList.add('express60-selected');
        } else {
          this.armed = false;
          if (this.hasExpress(items)) await this.removeFromCart();
          document.documentElement.classList.remove('express60-selected');
        }
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      } catch (e) {
        this.checkbox.checked = !wantOn; // revert UI on failure
      } finally {
        this.busy = false;
      }
    }

    async addToCart() {
      const body = {
        items: [{
          id: parseInt(this.variantId, 10),
          quantity: 1,
          properties: { _express60: 'true', _express_parent_variant_id: this.parentVariantId() }
        }]
      };
      const res = await fetch(`${this.cartRoute('cartAdd', '/cart/add')}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('add ' + res.status);
    }

    async removeFromCart() {
      const res = await fetch(`${this.cartRoute('cartChange', '/cart/change')}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(this.variantId), quantity: 0 })
      });
      if (!res.ok) throw new Error('change ' + res.status);
    }

    async cartItems() {
      const res = await fetch(`${this.cartRoute('cart', '/cart')}.js`, { cache: 'no-store' });
      if (!res.ok) throw new Error('cart ' + res.status);
      const cart = await res.json();
      return cart.items || [];
    }

    hasExpress(items) {
      return items.some((i) => String(i.id) === String(this.variantId));
    }

    hasProduct(items) {
      const pid = this.parentVariantId();
      return !!pid && items.some((i) => String(i.id) === pid);
    }

    /* Watch the cart drawer/items DOM and re-sync on any re-render (debounced). */
    observeCart() {
      const target = document.querySelector('cart-drawer') || document.getElementById('cart-items');
      if (!target || typeof MutationObserver === 'undefined') return;
      this.cartObserver = new MutationObserver(() => {
        if (this.syncTimer) clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.syncFromCart(), 250);
      });
      this.cartObserver.observe(target, { childList: true, subtree: true });
    }

    /* Serialised entry point: after an add-to-cart both the on:cart:add listener and the
       drawer MutationObserver call this. Run concurrently, both read a cart without the
       express line and both add it (qty 2, 100 AED). A call made while a sync is running
       is queued and re-runs once, against the cart the first sync left behind. */
    syncFromCart() {
      if (this.syncRunning) {
        this.syncQueued = true;
        return this.syncRunning;
      }
      this.syncRunning = this.runSync().finally(() => {
        this.syncRunning = null;
        if (this.syncQueued) {
          this.syncQueued = false;
          this.syncFromCart();
        }
      });
      return this.syncRunning;
    }

    /* Reflect the cart, and fulfil an armed selection once the product is added. */
    async runSync() {
      if (this.busy) return;
      let items;
      try {
        items = await this.cartItems();
      } catch (e) {
        return;
      }
      // handleChange() may have started while the cart was loading; it owns the cart now.
      if (this.busy) return;
      const expressInCart = this.hasExpress(items);
      const productInCart = this.hasProduct(items);

      // Armed + product just landed in the cart -> add the express line now.
      if (this.armed && productInCart && !expressInCart) {
        this.busy = true;
        try {
          await this.addToCart();
          this.armed = false;
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        } catch (e) {
          /* keep armed; retry on the next cart change */
        } finally {
          this.busy = false;
        }
        return;
      }

      if (this.checkbox) this.checkbox.checked = expressInCart || this.armed;
      document.documentElement.classList.toggle('express60-selected', expressInCart || this.armed);
    }

    /* The variant id of the product this toggle belongs to — read from the MAIN buy form
       only. The product section can hold other add-to-cart forms before it in DOM order
       ("Goes well with" recommendation cards, the instalments form), so the first
       `form [name="id"]` in the section is NOT reliably this product: on the PS5 Pro it
       was the Disc Drive card, so hasProduct() never matched the product that was added,
       the armed toggle never fulfilled and the express line never reached the cart. */
    parentVariantId() {
      const scope = this.closest('.shopify-section') || document;
      const input = scope.querySelector('form.js-product-form-main [name="id"]')
        || scope.querySelector('product-form form:not([id^="related-product"]) [name="id"]')
        || scope.querySelector('form [name="id"]');
      return input ? String(input.value) : '';
    }

    cartRoute(key, fallback) {
      return (window.theme && window.theme.routes && window.theme.routes[key]) || fallback;
    }

    /* Show the toggle only when the visitor's area allows it AND we're inside one
       of the configured availability windows. No windows configured -> always show
       (area permitting). */
    applyAvailabilityWindow() {
      if (!this.geoAllowed) {
        this.hidden = true;
        return;
      }
      const gstNow = this.getGSTDate();
      if (this.closedDays.indexOf(gstNow.getDay()) !== -1) {
        this.hidden = true; // office closed — no 60-minute runs today
        return;
      }
      const windows = gstNow.getDay() === 5 && this.fridayWindows.length
        ? this.fridayWindows
        : this.openWindows;
      if (!windows.length) {
        this.hidden = false;
        return;
      }
      const nowMin = gstNow.getHours() * 60 + gstNow.getMinutes();
      const open = windows.some((w) => e60WithinWindow(nowMin, w.from, w.to));
      this.hidden = !open;
    }

    getGSTDate() {
      const now = new Date();
      const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
      return new Date(utcMs + 4 * 3600000);
    }

    /* ---- Geo gating (reuses the shared cache / helper from express-delivery.js) ----
       Uses the EMIRATE resolver (manual pick beats IP, IP mapped through the emirate
       table) so the toggle can never contradict <delivery-eta> in the same buy box. */
    async isVisitorAllowed() {
      if (window.tbGeo && typeof window.tbGeo.resolveEmirate === 'function') {
        const resolved = await window.tbGeo.resolveEmirate();
        if (this.geoMode === 'uae') return resolved.inUae;
        return resolved.emirate === 'dubai';
      }
      if (window.tbGeo && typeof window.tbGeo.resolveRegion === 'function') {
        const region = await window.tbGeo.resolveRegion();
        return window.tbGeo.regionAllowed(region, this.geoMode);
      }
      const region = await this.lookupRegion();
      const country = (region.country || '').toUpperCase();
      const city = (region.city || '').toLowerCase();
      const area = (region.region || '').toLowerCase();
      if (country !== 'AE') return false;
      if (this.geoMode === 'uae') return true;
      return /dubai|دبي/.test(city) || /dubai|دبي/.test(area);
    }

    async lookupRegion() {
      let region;
      try {
        region = JSON.parse(sessionStorage.getItem('tb_geo_region'));
      } catch (e) {
        region = null;
      }
      if (region) return region;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000);
      try {
        const res = await fetch('https://ipwho.is/?fields=country_code,city,region,success', { signal: controller.signal });
        if (!res.ok) throw new Error('geo http ' + res.status);
        const d = await res.json();
        // Fail-safe: a failed lookup must NOT be cached — the whole delivery UI reads this
        // key for the rest of the session and an empty region would hide every promise.
        if (!d || d.success === false || !d.country_code) throw new Error('geo lookup failed');
        region = { country: d.country_code, city: d.city, region: d.region };
        try { sessionStorage.setItem('tb_geo_region', JSON.stringify(region)); } catch (e) { /* ignore */ }
        return region;
      } finally {
        clearTimeout(timer);
      }
    }
  }

  customElements.define('express-60-toggle', Express60Toggle);
}
